/**
 * Script de Vídeos Destacados - Divi Phergal
 * 
 * Gestiona la renderización, paginación y navegación de la galería
 * de vídeos destacados. Utiliza vanilla JavaScript puro sin dependencias
 * externas.
 * 
 * Características:
 * - Paginación por filas (página a página)
 * - Responsividad automática según breakpoints CSS
 * - Navegación por teclado (Tab, Enter, Arrow keys)
 * - Soporte para ARIA labels y atributos de accesibilidad
 * - Sin librerías externas: 100% vanilla JS
 * 
 * @package PhergalVideos
 * @since 1.0.0
 */

(function() {
    'use strict';

    /**
     * Objeto principal de la galería de vídeos
     * Encapsula toda la lógica en un IIFE para evitar contaminación del scope global
     */
    const VideosGallery = {
        /**
         * Almacena las instancias de carrouseles por ID
         * Permite múltiples galerías independientes en la misma página
         * @type {Object}
         */
        instances: {},

        /**
         * Configuración de breakpoints que coinciden con los CSS
         * Se comprueba con matchMedia para determinar el layout actual
         */
        breakpoints: {
            desktop: '(min-width: 1025px)',
            tablet: '(min-width: 769px) and (max-width: 1024px)',
            mobile: '(min-width: 481px) and (max-width: 768px)',
            smallMobile: '(max-width: 480px)'
        },

        /**
         * Mapeo de breakpoints a número de items por fila
         */
        itemsPerRowMap: {
            desktop: 4,
            tablet: 3,
            mobile: 2,
            smallMobile: 1
        },

        /**
         * Inicializa todas las galerías de vídeos en la página
         * Se ejecuta cuando el DOM está completamente cargado
         */
        init() {
            // Buscar todos los elementos de datos JSON de vídeos
            const videoDataElements = document.querySelectorAll('.vd-videos-data');
            
            // Inicializar cada galería
            videoDataElements.forEach((element) => {
                const carouselId = element.dataset.carouselId;
                if (carouselId) {
                    this.initCarousel(carouselId, element);
                }
            });

            // Escuchar cambios de tamaño de ventana para recompute de layout
            window.addEventListener('resize', () => this.handleResize());

            // Delegación de eventos para botones (Anterior/Siguiente)
            document.addEventListener('click', (e) => this.handleButtonClick(e));
            
            // Soporte de navegación por teclado
            document.addEventListener('keydown', (e) => this.handleKeyboard(e));
        },

        /**
         * Inicializa un carrousel individual
         * 
         * @param {string} carouselId - ID único del carrousel
         * @param {HTMLElement} dataElement - Elemento que contiene JSON de vídeos
         */
        initCarousel(carouselId, dataElement) {
            try {
                // Parsear datos JSON de vídeos
                const videosData = JSON.parse(dataElement.textContent);
                const { videos, parametters } = videosData;
               
                if (!Array.isArray(videos) || videos.length === 0) {
                    console.warn('[VideosGallery] No videos found for carousel:', carouselId);
                    return;
                }

                // Obtener el contenedor del carrousel
                const carouselContainer = document.querySelector(
                    `.vd-carousel[data-videos-per-row][data-index]`
                );

                if (!carouselContainer) {
                    console.warn('[VideosGallery] Carousel container not found for ID:', carouselId);
                    return;
                }

                // Crear instancia de carrousel
                const instance = {
                    id: carouselId,
                    container: carouselContainer,
                    videos: videos,
                    parametters: parametters,
                    currentIndex: 0,
                    itemsPerRow: this.getItemsPerRow(),
                    isDragging: false
                };

                // Almacenar la instancia
                this.instances[carouselId] = instance;

                // Renderizar vídeos inicialmente
                this.render(instance);

                // Actualizar estado de botones
                this.updateButtonStates(instance);

                //Eliminar el foco de otras cards al hacer hover en una card
                const cards = document.querySelectorAll('#videos-section-phergal_videos_module .vd-carousel .vd-card ');

                cards.forEach(card => {
                    card.addEventListener('mouseenter', () => {
                        const active = document.activeElement;

                        if (active && !card.contains(active)) {
                        active.blur();
                        }
                    });
                });

            } catch (error) {
                console.error('[VideosGallery] Error initializing carousel:', error);
            }
        },

        /**
         * Determina el número de items por fila según el breakpoint actual
         * Utiliza matchMedia para coincidencia de media queries
         * 
         * @returns {number} Número de items por fila (4, 3, 2 o 1)
         */
        getItemsPerRow() {
            for (const [breakpoint, itemsCount] of Object.entries(this.itemsPerRowMap)) {
                if (window.matchMedia(this.breakpoints[breakpoint]).matches) {
                    return itemsCount;
                }
            }
            // Fallback a desktop
            return 4;
        },

        /**
         * Renderiza los vídeos en el contenedor
         * Genera HTML para cada vídeo y maneja la visibilidad según paginación
         * 
         * @param {Object} instance - Instancia del carrousel
         */
        render(instance) {
            const { container, videos, parametters, currentIndex, itemsPerRow } = instance;

            // Limpiar contenedor
            container.innerHTML = '';

            // Calcular rango de vídeos visible
            const endIndex = Math.min(currentIndex + itemsPerRow, videos.length);

            // Generar HTML para cada vídeo
            videos.forEach((video, index) => {
                const isVisible = index >= currentIndex && index < endIndex;
                const cardHTML = this.createVideoCard(video, isVisible, parametters);
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = cardHTML;
                const article = tempDiv.firstElementChild;
                container.appendChild(article);
            });

            // Actualizar data-index para sincronización
            container.dataset.index = currentIndex;
        },

        /**
         * Genera el HTML de una tarjeta de vídeo
         * Incluye todos los elementos accesibles (aria-labels, roles, etc.)
         * 
         * @param {Object} video - Objeto con datos del vídeo
         * @param {boolean} isVisible - Si la tarjeta debe ser visible
         * @returns {string} HTML de la tarjeta
         */
        createVideoCard(video, isVisible, parametters) {
            // Formatear fecha
            const dateObj = new Date(video.date);
            const formatter = new Intl.DateTimeFormat('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
            const formattedDate = formatter.format(dateObj);
            
            // Clases de visibilidad
            const hiddenClass = isVisible ? '' : 'vd-hidden';

            // Constructor HTML con template literals
            return `
                <article class="vd-card ${hiddenClass}" role="listitem">
                    <!-- Miniatura clickeable con overlay -->
                    <a 
                        href="${this.escapeHtml(video.url)}"
                        class="vd-thumb" target="_blank" rel="noopener noreferrer"
                        aria-label="Reproducir: ${this.escapeHtml(video.title)}"
                    >
                        <img 
                            src="${this.escapeHtml(video.thumbnail)}"
                            alt="Miniatura: ${this.escapeHtml(video.title)}"
                            loading="lazy"
                            width="640"
                            height="360"
                        />
                        . ${parametters.show_duration ? `<span class="vd-duration">${this.escapeHtml(video.duration)}</span>` : ''}            
                        <span class="vd-play" aria-hidden="true">▶</span>
                    </a>

                    <!-- Contenido: título y metadatos -->
                    <div class="vd-content">
                        <h3 class="vd-title">${this.escapeHtml(video.title)}</h3>
                        <p class="vd-meta">
                            ${parametters.show_brand ? `<span class="vd-brand">${this.escapeHtml(video.brandname)}</span> · ` : ''}
                            <time datetime="${this.escapeHtml(video.date)}">${formattedDate}</time>
                            ${parametters.show_views ? ` · ${this.escapeHtml(video.views)} vistas` : ''}
                        </p>
                    </div>
                </article>
            `;
        },

        /**
         * Maneja clics en botones Anterior/Siguiente
         * Actualiza el índice y re-renderiza la galería
         * 
         * @param {Event} event - Evento de click
         */
        handleButtonClick(event) {
            const button = event.target.closest('.vd-button');
            if (!button) return;

            const carouselId = button.dataset.carouselId;
            const instance = this.instances[carouselId];

            if (!instance) return;

            const { videos, itemsPerRow } = instance;
            const totalPages = Math.ceil(videos.length / itemsPerRow);

            if (button.classList.contains('vd-prev')) {
                // Botón Anterior
                if (instance.currentIndex > 0) {
                    instance.currentIndex = Math.max(0, instance.currentIndex - itemsPerRow);
                    this.render(instance);
                    this.updateButtonStates(instance);
                    this.announceToScreenReader(
                        `Viendo vídeos ${instance.currentIndex + 1} al ${Math.min(instance.currentIndex + itemsPerRow, videos.length)} de ${videos.length}`
                    );
                }
            } else if (button.classList.contains('vd-next')) {
                // Botón Siguiente
                const newIndex = instance.currentIndex + itemsPerRow;
                if (newIndex < videos.length) {
                    instance.currentIndex = newIndex;
                    this.render(instance);
                    this.updateButtonStates(instance);
                    this.announceToScreenReader(
                        `Viendo vídeos ${instance.currentIndex + 1} al ${Math.min(instance.currentIndex + itemsPerRow, videos.length)} de ${videos.length}`
                    );
                }
            }
        },

        /**
         * Actualiza el estado (activado/desactivado) de los botones
         * Basado en la posición actual en la paginación
         * 
         * @param {Object} instance - Instancia del carrousel
         */
        updateButtonStates(instance) {
            const { videos, itemsPerRow, currentIndex, id } = instance;

            const prevButton = document.querySelector(`.vd-prev[data-carousel-id="${CSS.escape(id)}"]`);
            const nextButton = document.querySelector(`.vd-next[data-carousel-id="${CSS.escape(id)}"]`);

            if (prevButton) {
                prevButton.disabled = currentIndex <= 0;
            }

            if (nextButton) {
                nextButton.disabled = currentIndex + itemsPerRow >= videos.length;
            }
        },

        /**
         * Maneja eventos de teclado para navegación accesible
         * - Enter/Espacio en botones: activar
         * - Arrow Left/Right: navegar galerías
         * 
         * @param {KeyboardEvent} event - Evento de teclado
         */
        handleKeyboard(event) {
            // Si es Enter o Espacio en un botón, dejar que el navegador lo maneje
            if ((event.key === 'Enter' || event.key === ' ') && 
                event.target.closest('.vd-button')) {
                return; // Comportamiento por defecto
            }

            // Arrow keys para navegación entre tarjetas (opcional)
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                const activeCarousel = document.activeElement.closest('.vd-carousel');
                if (activeCarousel) {
                    // Aquí podrías añadir lógica adicional si lo deseas
                }
            }
        },

        /**
         * Maneja el redimensionamiento de ventana
         * Recalcula el layout y re-renderiza si es necesario
         */
        handleResize() {
            const newItemsPerRow = this.getItemsPerRow();

            // Recalcular todas las instancias
            Object.values(this.instances).forEach((instance) => {
                if (instance.itemsPerRow !== newItemsPerRow) {
                    // El layout ha cambiado, ajustar y re-renderizar
                    instance.itemsPerRow = newItemsPerRow;
                    
                    // Ajustar índice si es necesario
                    if (instance.currentIndex >= instance.videos.length - 1) {
                        instance.currentIndex = 0;
                    }
                    
                    this.render(instance);
                    this.updateButtonStates(instance);
                }
            });
        },

        /**
         * Anuncia cambios a lectores de pantalla usando aria-live
         * Útil para informar sobre cambios de paginación
         * 
         * @param {string} message - Mensaje a anunciar
         */
        announceToScreenReader(message) {
            // Crear elemento aria-live si no existe
            let liveRegion = document.getElementById('vd-aria-live');
            if (!liveRegion) {
                liveRegion = document.createElement('div');
                liveRegion.id = 'vd-aria-live';
                liveRegion.setAttribute('aria-live', 'polite');
                liveRegion.setAttribute('aria-atomic', 'true');
                liveRegion.className = 'vd-sr-only';
                document.body.appendChild(liveRegion);
            }

            liveRegion.textContent = message;

            // Limpiar mensaje después de 3 segundos
            setTimeout(() => {
                liveRegion.textContent = '';
            }, 3000);
        },

        /**
         * Escapa caracteres especiales HTML para evitar XSS
         * Utilizado en template literals para sanitizar datos
         * 
         * @param {string} text - Texto a escapar
         * @returns {string} Texto escapado
         */
        escapeHtml(text) {
            const map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            };
            return String(text).replace(/[&<>"']/g, (char) => map[char]);
        }
    };

    /**
     * Pagina listado de videos al cargar la página
     */
    const VideosGalleryPage = {
        headerBrands : document.querySelector('.phergal-videos .header-categories'),
        toggleBtn : document.querySelector('.phergal-videos .toggle-expand'),
        categoryItemsList : document.querySelectorAll('.phergal-videos .header-categories .category-item'),
        currentCategory : null,
        currentItems : 20,
        isExpanded : false,
        videosParametters : null,

        init() {
            // Toggle al hacer click en el botón
            if (this.toggleBtn && this.headerBrands && this.categoryItemsList.length > 1) {
                this.toggleBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.isExpanded = !this.isExpanded;
                    
                    // Toggle visibility de items con transición suave
                    if (this.isExpanded) {
                            this.expandedBrandMenu();
                    } else {
                            this.collapseBrandMenu();
                    }
                });
            }
            
            // Click sobre marca
            this.categoryItemsList.forEach((item) => {
                item.addEventListener('click', (e) => {
                    e.preventDefault();
                    
                    // Reorganizar menu de categorías en mobile
                    if (this.isMobile()) {
                            const headerCategoryContainer = document.querySelector('.phergal-videos .header-categories');
                            if (headerCategoryContainer && item.parentElement === headerCategoryContainer) {
                                // Mover el item clickeado al inicio
                                headerCategoryContainer.insertBefore(item, headerCategoryContainer.firstChild);
                                
                                // Actualizar referencia de categoryItemsList
                                this.categoryItemsList = document.querySelectorAll('.phergal-videos .header-categories .category-item');
                                
                                // Refrescar visibilidad de items
                                this.initializeCategoryVisibility();
                                
                                // Contraer el menú
                                this.collapseBrandMenu();
                            }
                    }
                    
                    const categoryId = item.getAttribute('data-category');
                    const items = parseInt(item.getAttribute('data-category-total-items')) || this.currentItems;
                    this.loadCategoryRecords(categoryId, 1, items);
                });
            });

            //Actualizar los parametros de visualización de vídeos desde el script inline
            const videoDataParametters = document.querySelector('.vd-videos-parametters');
            if (videoDataParametters) {
                this.videosParametters = JSON.parse(videoDataParametters.textContent);
            }
            
            this.initPhergalInitialLoad();
            this.initializeCategoryVisibility();

            // Actualizar visibilidad al redimensionar
            window.addEventListener('resize', () => {
                this.initializeCategoryVisibility();
            });
        },

        // Función para detectar si es mobile
        isMobile() {
            return window.innerWidth < 768;
        },
        
        // Función para ocultar items después del primero en mobile
        initializeCategoryVisibility() {
            if (this.isMobile() && this.categoryItemsList.length > 1) {
                this.categoryItemsList.forEach((item, index) => {
                        if (index > 0) {
                            item.classList.add('hidden');
                        } else {
                            item.classList.remove('hidden');
                        }
                });
                this.isExpanded = false;
                if (this.toggleBtn) this.toggleBtn.style.display = 'block';
            } else {
                this.categoryItemsList.forEach((item) => {
                        item.classList.remove('hidden');
                });
                this.isExpanded = true;
                if (this.toggleBtn) this.toggleBtn.style.display = 'none';
            }
        },

        // Contraer el menú de categorías
        collapseBrandMenu() {            
            this.isExpanded = false;
            this.categoryItemsList.forEach((item, index) => {
                if (index > 0) {
                        item.classList.add('hidden');
                        setTimeout(() => {
                            item.style.display = 'none';
                        }, 300);
                }
            });
            const icon = this.toggleBtn.querySelector('.toggle-text');
            if (icon) {
                icon.textContent = '↓ Ver más Categorías';
            }
        },

        // Expandir el menú de categorías
        expandedBrandMenu() {
            this.isExpanded = true;
            this.categoryItemsList.forEach((item, index) => {
                if (index > 0) {
                        // Mostrar: primero display, luego quitar la clase hidden para transición
                        item.style.display = 'block';
                        setTimeout(() => {
                            item.classList.remove('hidden');
                        }, 10);
                }
            });
            const icon = this.toggleBtn.querySelector('.toggle-text');
            if (icon) {
                icon.textContent = '↑ Ver menos Categorías';
            }
        },
        
        loadCategoryRecords(categoryId, page = 1, items = 20) {
            this.currentCategory = categoryId;
            this.currentItems = items;

            fetch(phergalVideosData.ajaxurl, {
                method: 'POST',
                headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                        action: 'get_category_records',
                        category_id: this.currentCategory,
                        items: this.currentItems,
                        video_parametters: JSON.stringify(this.videosParametters),
                        page: page,
                        nonce: phergalVideosData.nonce
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                        const listContainer = document.querySelector('#phergal-videos-page .dtq-post-list-parent');
                        const paginationContainer = document.querySelector('#phergal-videos-page .phergal-pagination');
                        const categoryList = document.querySelectorAll('.phergal-videos .header-categories .category-item');

                        // Actualizar estado activo en categorías
                        categoryList.forEach(function(item) {
                            item.classList.remove('active');
                        });
                        const activeCategory = document.querySelector(`.phergal-videos .header-categories .category-item[data-category="${this.currentCategory}"]`);
                        if (activeCategory) {
                            activeCategory.classList.add('active');
                        }

                        if (listContainer) {
                            listContainer.innerHTML = data.data.html;
                        }

                        const cards = document.querySelectorAll('#phergal-videos-page .vd-carousel .vd-card');

                        cards.forEach(card => {
                            card.addEventListener('mouseenter', () => {
                                const active = document.activeElement;

                                if (active && !card.contains(active)) {
                                    active.blur();
                                }
                            });
                        });

                        if (paginationContainer) {
                            // Insertar HTML de paginación generado por WP
                            paginationContainer.innerHTML = data.data.pagination || '';

                            // Interceptar clicks en enlaces de paginación generados por WP
                            paginationContainer.querySelectorAll('a').forEach((link) => {
                                link.addEventListener('click', (ev) => {
                                    ev.preventDefault();
                                    const linkElement = ev.currentTarget;
                                    const href = linkElement.getAttribute('href') || '';

                                    // Extraer número de página desde href (paged=X o /page/X/)
                                    let pageMatch = href.match(/[?&]paged=(\d+)/i) || href.match(/page\/(\d+)\/?/i);
                                    let page = 1;
                                    if (pageMatch && pageMatch[1]) {
                                            page = parseInt(pageMatch[1], 10);
                                    }

                                    // Intentar extraer category_id de querystring si existe
                                    const urlParams = new URLSearchParams(href.split('?')[1] || '');
                                    const categoryFromHref = urlParams.get('category');
                                    const categoryToUse = categoryFromHref || this.currentCategory;

                                    if (categoryToUse) {
                                            this.loadCategoryRecords(categoryToUse, page, this.currentItems);
                                    }
                                });
                            });
                        }
                    
                } else {
                        console.error('Error:', data.data.message);
                }
            })
            .catch(error => console.error('Error en la petición:', error));
        },

        initPhergalInitialLoad(){
          this.loadCategoryRecords(1, 1, this.currentItems);
        }
    };


    /**
     * Inicializador: espera a que el DOM esté listo
     * Soporta múltiples estados de documento
     */
    function initWhenReady() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                VideosGallery.init();
                VideosGalleryPage.init();
            });
        } else {
            // DOM ya está cargado (en AJAX, page reload, etc.)
            VideosGallery.init();
            VideosGalleryPage.init();
        }
    }

    // Iniciar cuando esté listo
    initWhenReady();

    // Exportar para acceso externo si es necesario
    if (typeof window !== 'undefined') {
        window.VideosGallery = VideosGallery;
    }

})();
